/**
 * Pure helpers for the outcome of a release change (register, make-current, withdraw, resync):
 * plain refusal messages and the two non-blocking sync warnings. Tested in releaseChange.test.ts.
 */
import type { GuardianSync, ManifestSync } from '../api/releases';

/** Plain wording for refusals the API returns by code. Anything else shows the API's own message. */
const REFUSALS: Record<string, string> = {
  PACKAGE_CHANGED: 'This release\'s package changed after it was checked. Publish it again.',
  VERSION_NOT_NEWER: 'Only a version newer than the current release can be made current.',
  BAD_SHA: 'The package check result was not valid. Upload and check the package again.',
  NOT_CHECKED: 'The checked package was not found. Upload and check it again.',
};

/** The message to show for a failed release call. */
export function releaseErrorMessage(e: unknown): string {
  const err = (e ?? {}) as { code?: unknown; message?: unknown };
  if (typeof err.code === 'string' && Object.prototype.hasOwnProperty.call(REFUSALS, err.code)) return REFUSALS[err.code];
  return typeof err.message === 'string' && err.message ? err.message : 'The request failed.';
}

export interface GuardianWarning { error: string | null; notSentCount: number }

/** Warnings from the last change. Both are non-blocking: the change itself was saved. */
export interface SyncWarnings {
  /** Full sentence for the manifest warning, or null when the manifest is in step. */
  manifest: string | null;
  guardian: GuardianWarning | null;
}

export const NO_SYNC_WARNINGS: SyncWarnings = { manifest: null, guardian: null };

export function describeSync(change: { manifestSync?: ManifestSync | null; guardianSync?: GuardianSync | null }): SyncWarnings {
  const m = change.manifestSync;
  const g = change.guardianSync;
  let manifest: string | null = null;
  if (m && m.ok === false) {
    const detail = typeof m.message === 'string' ? m.message.trim().replace(/\.+$/, '') : '';
    manifest = `Release saved, but the deployment manifest was not updated${detail ? `: ${detail}` : ''}. `
      + 'New deployments may still use the previous release. Use Resync.';
  }
  const guardian = g && g.ok === false
    ? { error: g.error ?? null, notSentCount: Array.isArray(g.notSent) ? g.notSent.length : 0 }
    : null;
  return { manifest, guardian };
}

export function hasSyncWarnings(w: SyncWarnings): boolean {
  return w.manifest !== null || w.guardian !== null;
}
